import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AddOnItemName } from "schemas/dashboard";
import { routes } from "schemas/routes";
import { LIGHT_GREEN, LIGHT_GREY } from "styles/oneOffColor";

import { useAccountReviewState } from "features/Auth/Onboarding/shared/AccountReviewUpsell";
import { fetcherAuth } from "utils/api";
import { getParams, getSource } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CardWrapper from "../components/CardWrapper";

enum OnboardingReviewRequestStatus {
  pending = "pending",
  fulfilled = "fulfilled",
}

interface OnboardingReviewRequest {
  status: OnboardingReviewRequestStatus;
  isArchived: boolean;
}

const REVIEW_FEATURES = [
  {
    emoji: "🎯",
    title: "Campaign & automation review",
    description:
      "We'll go through your campaigns and any automation set up to make sure they're working hard for you.",
  },
  {
    emoji: "📝",
    title: "Template review",
    description:
      "A CSM checks your template set up so every pitch lands the right way.",
  },
  {
    emoji: "⚙️",
    title: "Profile & settings review",
    description:
      "We'll review your profile and settings set up so your account is fully dialed in.",
  },
];

const AddOnCustomerSupport = () => {
  const source = getSource();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, addOns, setOpenCheckoutDialog, upgradeDialogSource } =
    useContext(SubscriptionContext);
  const { checkoutLoading } = useSubscription();
  const { hasPurchasedAccountReview } = useAccountReviewState();

  const [request, setRequest] = useState<OnboardingReviewRequest | null>(null);
  const [fetchLoading, setFetchLoading] = useState(true);

  useEffect(() => {
    if (!currentOrg?.id || !hasPurchasedAccountReview) {
      setFetchLoading(false);
      return;
    }
    const fetchRequest = async () => {
      setFetchLoading(true);
      try {
        const res = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/onboarding-review-requests`,
        );
        setRequest(res?.onboardingReviewRequest ?? null);
      } catch (error) {
        setErrorAlert(error);
      } finally {
        setFetchLoading(false);
      }
    };
    fetchRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, hasPurchasedAccountReview]);

  const hasActiveRequest = request && !request.isArchived;
  const isPending =
    hasActiveRequest &&
    request.status === OnboardingReviewRequestStatus.pending;
  const needsToSubmitRequest = hasPurchasedAccountReview && !hasActiveRequest;

  const handleCheckout = async () => {
    if (!plans || !addOns) return;
    const state: CheckoutState = {
      plan: AddOnItemName.customer_support_settings,
      source,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    if (upgradeDialogSource) {
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  const renderDisabledWithNote = (note: string) => (
    <>
      <LoadingButton
        disabled
        disableElevation
        variant="contained"
        sx={{
          px: 3,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        Purchase
      </LoadingButton>
      <Typography color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
        {note}
      </Typography>
    </>
  );

  const renderActionArea = () => {
    if (hasPurchasedAccountReview && fetchLoading) {
      return <CircularProgress size={24} sx={{ color: "primary.main" }} />;
    }

    if (needsToSubmitRequest) {
      return renderDisabledWithNote(
        "Please complete onboarding and submit request our team to take a look at your account.",
      );
    }

    if (isPending) {
      return renderDisabledWithNote(
        "Your request is pending. We'll review it within 3 business days.",
      );
    }

    return (
      <>
        <LoadingButton
          onClick={handleCheckout}
          disableElevation
          variant="contained"
          loading={checkoutLoading === AddOnItemName.customer_support_settings}
          sx={{
            px: 3,
            color: "white",
            backgroundColor: "primary.main",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Purchase
        </LoadingButton>
        <Typography color="text.secondary" sx={{ mt: 2, fontStyle: "italic" }}>
          Review delivered within <strong>3 business days</strong>.
        </Typography>
      </>
    );
  };

  return (
    <CardWrapper>
      <Grid item>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" component="div">
            🧑‍💼 Account Setup Review by a CSM
          </Typography>
          <Chip
            label="NEW"
            size="small"
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          />
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mt: 1.5 }}
        >
          <Typography
            variant="h5"
            sx={{ color: "green.main", fontWeight: 700 }}
          >
            $
            {addOns?.[AddOnItemName.customer_support_settings]?.price.toFixed(
              2,
            )}
          </Typography>
          <Chip
            label="One-time fee"
            size="small"
            sx={{ backgroundColor: LIGHT_GREEN, color: "green.main" }}
          />
        </Stack>

        <Typography sx={{ mt: 2 }}>
          Get your account reviewed by a real Customer Success Manager at Bento
          — a human expert who helps you maximize the use of Bento.
        </Typography>

        <Stack spacing={2} sx={{ mt: 3 }}>
          {REVIEW_FEATURES.map((feature) => (
            <Stack key={feature.title} direction="row" spacing={2}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: LIGHT_GREY,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.25rem",
                }}
              >
                {feature.emoji}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {feature.title}
                </Typography>
                <Typography color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Grid>

      <Grid item sx={{ mt: 3 }}>
        {renderActionArea()}
      </Grid>
    </CardWrapper>
  );
};

export default AddOnCustomerSupport;
